<template>
  <div class="flex w-full flex-col gap-11">
    <div class="flex flex-row items-center justify-between">
      <span class="text-3xl font-medium sm:text-5xl">Pipelines</span>
      <Button
        class="hover-lift flex items-center gap-1.5"
        type="submit"
        transition="transition-all"
        @click="redirectToPage('redesign.company.pipelines.create')"
      >
        <span>
          {{ __('New Pipeline') }}
        </span>
      </Button>
    </div>

    <div
      class="flex h-full min-h-[907px] w-full flex-col rounded-lg bg-white p-6 drop-shadow-md"
    >
      <div class="flex w-full flex-row items-start gap-4">
        <!-- All Radio -->
        <label class="flex cursor-pointer items-center">
          <input
            type="radio"
            name="status"
            value=""
            :checked="status === null || status === ''"
            class="mr-2 border-primary text-primary focus:shadow-none focus:outline-none focus:ring-0 focus:ring-offset-0"
            @change="handleChange('')"
          />
          {{ __('All') }}
        </label>

        <!-- Active Radio -->
        <label class="flex cursor-pointer items-center">
          <input
            type="radio"
            name="status"
            value="active"
            :checked="status === 'active'"
            class="mr-2 border-primary text-primary focus:shadow-none focus:outline-none focus:ring-0 focus:ring-offset-0"
            @change="handleChange('active')"
          />
          {{ __('Active') }}
        </label>

        <!-- Inactive Radio -->
        <label class="flex cursor-pointer items-center">
          <input
            type="radio"
            name="status"
            value="inactive"
            :checked="status === 'inactive'"
            class="mr-2 border-primary text-primary focus:shadow-none focus:outline-none focus:ring-0 focus:ring-offset-0"
            @change="handleChange('inactive')"
          />
          {{ __('Inactive') }}
        </label>
      </div>
      <div class="mt-3 w-full">
        <Input
          v-model="searchString"
          :value="searchString"
          :placeholder="__('Search pipelines...')"
          type="text"
          :maxlength="100"
          aria-label="Search pipelines"
          @input="handleSearchInput"
        />
      </div>

      <div
        class="mt-8 grid w-full grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4"
      >
        <button
          type="button"
          class="hover-lift flex min-h-[206px] flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-primary transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
          aria-label="Create new pipeline"
          @click="redirectToPage('redesign.company.pipelines.create')"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="25"
            height="24"
            viewBox="0 0 25 24"
            fill="none"
          >
            <mask
              id="mask0_5582_205"
              style="mask-type: alpha"
              maskUnits="userSpaceOnUse"
              x="0"
              y="0"
              width="25"
              height="24"
            >
              <rect x="0.5" width="24" height="24" class="fill-primary" />
            </mask>
            <g mask="url(#mask0_5582_205)">
              <path
                d="M11.5 13V16C11.5 16.2833 11.5958 16.5208 11.7875 16.7125C11.9792 16.9042 12.2167 17 12.5 17C12.7833 17 13.0208 16.9042 13.2125 16.7125C13.4042 16.5208 13.5 16.2833 13.5 16V13H16.5C16.7833 13 17.0208 12.9042 17.2125 12.7125C17.4042 12.5208 17.5 12.2833 17.5 12C17.5 11.7167 17.4042 11.4792 17.2125 11.2875C17.0208 11.0958 16.7833 11 16.5 11H13.5V8C13.5 7.71667 13.4042 7.47917 13.2125 7.2875C13.0208 7.09583 12.7833 7 12.5 7C12.2167 7 11.9792 7.09583 11.7875 7.2875C11.5958 7.47917 11.5 7.71667 11.5 8V11H8.5C8.21667 11 7.97917 11.0958 7.7875 11.2875C7.59583 11.4792 7.5 11.7167 7.5 12C7.5 12.2833 7.59583 12.5208 7.7875 12.7125C7.97917 12.9042 8.21667 13 8.5 13H11.5ZM5.5 21C4.95 21 4.47917 20.8042 4.0875 20.4125C3.69583 20.0208 3.5 19.55 3.5 19V5C3.5 4.45 3.69583 3.97917 4.0875 3.5875C4.47917 3.19583 4.95 3 5.5 3H19.5C20.05 3 20.5208 3.19583 20.9125 3.5875C21.3042 3.97917 21.5 4.45 21.5 5V19C21.5 19.55 21.3042 20.0208 20.9125 20.4125C20.5208 20.8042 20.05 21 19.5 21H5.5Z"
                class="fill-primary"
              />
            </g>
          </svg>

          <span class="text-primary">{{ __('New Pipeline') }}</span>
        </button>

        <!-- Skeleton loader -->
        <template v-if="loading">
          <div
            v-for="i in 15"
            :key="`skeleton-${i}`"
            class="flex h-[206px] animate-pulse flex-col items-center justify-center gap-6 rounded-2xl border border-zinc-300 p-6"
            role="status"
            aria-label="Loading pipeline"
          >
            <div class="h-6 w-3/4 rounded bg-gray-200"></div>
            <div class="h-4 w-1/2 rounded bg-gray-200"></div>
            <div class="h-4 w-2/3 rounded bg-gray-200"></div>
          </div>
        </template>

        <!-- Pipeline cards -->
        <div
          v-else
          v-for="pipeline in pipelines.data"
          :key="pipeline.id"
          class="hover-lift flex min-h-[206px] cursor-pointer flex-col items-center justify-between gap-6 rounded-2xl border border-zinc-300 p-6 transition-all focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
          tabindex="0"
          role="button"
          :aria-label="`View pipeline: ${pipeline.name}`"
          @click="redirectToPage('redesign.company.pipelines.show', pipeline)"
          @keydown.enter="
            redirectToPage('redesign.company.pipelines.show', pipeline)
          "
          @keydown.space.prevent="
            redirectToPage('redesign.company.pipelines.show', pipeline)
          "
        >
          <div class="flex w-full flex-row items-start justify-between gap-x-2">
            <div class="min-w-0 flex-1">
              <div class="line-clamp-3 break-words text-base font-medium">
                {{ pipeline.name }}
              </div>
            </div>

            <div class="relative">
              <PipelinesActionsPopup
                :pipeline-id="pipeline.id"
                :status="Boolean(pipeline.status)"
                :is-owner="pipeline.is_owner"
                :is-open="openPopupMenuId === pipeline.id"
                @edit="handlePipelineEdit"
                @delete="handlePipelineDelete"
                @toggle-status="handlePipelineEditStatus"
                @toggle="handlePopupToggle(pipeline.id)"
              />
            </div>
          </div>

          <div class="flex w-full flex-row items-end justify-between">
            <div class="flex flex-col gap-1">
              <span class="text-3xl font-medium">{{
                pipeline.candidates.length
              }}</span>
              <span class="text-sm text-zinc-600">{{ __('Candidates') }}</span>
            </div>

            <FadeTransition>
              <div
                v-if="pipeline.status"
                class="flex items-center justify-center rounded-lg bg-status-active/10 px-3.5 py-0.5"
              >
                <span class="text-base font-medium text-status-active">
                  {{ __('Active') }}
                </span>
              </div>

              <div
                v-else
                class="flex items-center justify-center rounded-lg bg-status-inactive/10 px-3.5 py-0.5"
              >
                <span class="text-base font-medium text-status-inactive">
                  {{ __('Inactive') }}
                </span>
              </div>
            </FadeTransition>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div class="mt-auto">
        <Pagination
          v-if="pipelines.meta?.last_page > 1"
          :key="paginationKey"
          :current-page="pipelines.meta.current_page"
          :last-page="pipelines.meta.last_page"
          class="mt-6"
          @update:current-page="handlePageChange"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
  import axios from 'axios';
  import { nextTick, onMounted, onUnmounted, ref } from 'vue';
  import { useToast } from 'vue-toastification';

  import { __ } from '@/helpers';
  import { Button } from '../../../components/block/button';
  import FadeTransition from '../../../components/global/FadeTransition.vue';
  import Input from '../../../components/global/Form/Input.vue';
  import Pagination from '../../../components/global/Pagination.vue';
  import PipelinesActionsPopup from './components/PipelinesActionsPopup.vue';

  const props = defineProps({
    data: {
      type: Object,
      required: true
    },
    errors: {
      type: Object,
      default: () => ({})
    }
  });

  const toast = useToast();
  const pipelines = ref(props.data.pipelines || []);
  const paginationKey = ref(0);
  const status = ref(null);
  const searchString = ref('');
  const loading = ref(false);
  const openPopupMenuId = ref(null);

  let searchTimeout = null;
  let clickOutsideHandler = null;

  onMounted(async () => {
    await nextTick();
    showErrorMessages();
    showSessionMessages();
    setupClickOutsideHandler();
  });

  onUnmounted(() => {
    clearTimeout(searchTimeout);
    if (clickOutsideHandler) {
      document.removeEventListener('click', clickOutsideHandler);
    }
  });

  const handlePageChange = async (page) => {
    const perPage = 15;
    loading.value = true;

    try {
      const response = await axios.get(
        route('redesign.company.pipelines.load'),
        {
          params: {
            perPage,
            page,
            status: status.value,
            searchString: searchString.value
          }
        }
      );

      setTimeout(() => {
        pipelines.value = response.data.data.pipelines;
        paginationKey.value++;
        loading.value = false;
      }, 300);
    } catch (error) {
      console.error('Error fetching the pipelines:', error);
      setTimeout(() => {
        loading.value = false;
        status.value = null;
        searchString.value = null;
        toast.error(
          error.response?.data?.message || __('Something went wrong')
        );
      }, 2000);
    }
  };
  const handleChange = (statusValue) => {
    status.value = status.value === statusValue ? null : statusValue;
    handlePageChange(1);
  };

  const redirectToPage = (name, params) => {
    window.location.href = route(name, params);
  };

  const handleSearchInput = () => {
    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => {
      handlePageChange(1);
    }, 300);
  };

  const handlePipelineEdit = (pipelineId) => {
    redirectToPage('redesign.company.pipelines.edit', pipelineId);
  };
  const handlePipelineDelete = async (pipelineId) => {
    try {
      const response = await axios.delete(
        route('redesign.company.pipelines.destroy', pipelineId)
      );
      setTimeout(() => {
        toast.success(
          response?.data?.message || __('Pipeline deleted successfully')
        );
        handlePageChange(1);
      }, 750);
    } catch (error) {
      toast.error(error.response?.data?.message || __('Something went wrong'));
    }
  };
  const handlePipelineEditStatus = async ({ pipelineId, status_value }) => {
    try {
      const pipeline = pipelines.value.data.find((p) => p.id === pipelineId);
      if (!pipeline) return;

      const response = await axios.patch(
        route('redesign.company.pipelines.toggle-status', pipelineId),
        {
          status: status_value,
          updated_at: pipeline.updated_at
        }
      );

      toast.success(
        response?.data?.message || __('Pipeline status modified successfully')
      );

      const index = pipelines.value.data.findIndex((p) => p.id === pipelineId);
      if (index !== -1) {
        pipelines.value.data[index].status = response.data.status;
        pipelines.value.data[index].updated_at = response.data.updated_at;

        if (status.value !== response.data.status && status.value !== null) {
          setTimeout(() => {
            handlePageChange(1);
          }, 750);
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.message || __('Something went wrong'));
    }
  };

  const showErrorMessages = () => {
    const errorMessages = Object.values(props.errors).flat();
    if (errorMessages.length > 0) {
      toast.error(errorMessages[0]);
    }
  };
  const showSessionMessages = () => {
    const sessionMessage = sessionStorage.getItem('toast_message');
    if (sessionMessage) {
      try {
        const { type, message } = JSON.parse(sessionMessage);
        toast[type](message);
        sessionStorage.removeItem('toast_message');
      } catch (error) {
        console.error('Error parsing session message:', error);
        sessionStorage.removeItem('toast_message');
      }
    }
  };
  const handlePopupToggle = (pipelineId) => {
    openPopupMenuId.value =
      openPopupMenuId.value === pipelineId ? null : pipelineId;
  };

  const setupClickOutsideHandler = () => {
    clickOutsideHandler = (e) => {
      if (!e.target.closest('[data-menu]')) {
        openPopupMenuId.value = null;
      }
    };
    document.addEventListener('click', clickOutsideHandler);
  };
</script>
